import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

// Placeholder imports for the sub-forms (we'll create these next)
import { CalAimEntryForm } from "./components/CalAimEntryForm";
import { CoordinatedCareEntryForm } from "./components/CoordinatedCareEntryForm";
import { GeneralNoteEntryForm } from "./components/GeneralNoteEntryForm";
import { AddBookingEntryForm } from "./components/AddBookingEntryForm"; // New import
import { KeyEventEntryForm } from "./components/KeyEventEntryForm"; // New import
import { DocumentEntryForm } from "./components/DocumentEntryForm";

interface AddEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  odrNumber: string;
  participantId: string; // Usually programNumber or similar
  user: any;
  refetch: () => void;
  allowedEntryTypes?: EntryType[];
  initialEntryType?: EntryType;
}

export type EntryType =
  | "calaim"
  | "coordinated_care"
  | "general_note"
  | "booking"
  | "key_event"
  | "document"
  | "";

const ENTRY_TYPE_OPTIONS: Array<{ value: EntryType; label: string }> = [
  { value: "booking", label: "New Booking" },
  { value: "document", label: "Document" },
  { value: "calaim", label: "ECM Note" },
  { value: "coordinated_care", label: "Coordinated Care Note" },
  { value: "general_note", label: "General Note" },
  { value: "key_event", label: "Key Event" }
];

const isEntryTypeAllowed = (value: EntryType, allowedEntryTypes?: EntryType[]) => {
  if (!allowedEntryTypes || allowedEntryTypes.length === 0) {
    return true;
  }

  return allowedEntryTypes.includes(value);
};

export const AddEntryModal: React.FC<AddEntryModalProps> = ({
  isOpen,
  onClose,
  odrNumber,
  participantId,
  user,
  refetch,
  allowedEntryTypes,
  initialEntryType
}) => {
  const [entryType, setEntryType] = useState<EntryType>("");
  const hasLockedEntryType = Boolean(
    initialEntryType && isEntryTypeAllowed(initialEntryType, allowedEntryTypes)
  );

  useEffect(() => {
    if (!isOpen) return;
    if (!initialEntryType) {
      setEntryType("");
      return;
    }
    if (!isEntryTypeAllowed(initialEntryType, allowedEntryTypes)) {
      setEntryType("");
      return;
    }
    setEntryType(initialEntryType);
  }, [allowedEntryTypes, initialEntryType, isOpen]);

  const handleTypeChange = (value: string) => {
    const nextType = value as EntryType;
    if (!isEntryTypeAllowed(nextType, allowedEntryTypes)) {
      return;
    }

    setEntryType(nextType);
  };

  const getDialogTitle = () => {
    if (entryType && !isEntryTypeAllowed(entryType, allowedEntryTypes)) {
      return "Add New Entry";
    }

    switch (entryType) {
      case "calaim":
        return "Add New ECM Note";
      case "coordinated_care":
        return "Add New Coordinated Care Note";
      case "general_note":
        return "Add New General Note";
      case "booking":
        return "Add New Booking";
      case "key_event":
        return "Add Key Event";
      case "document":
        return "Add Document";
      default:
        return "Add New Entry";
    }
  };

  const renderForm = () => {
    if (entryType && !isEntryTypeAllowed(entryType, allowedEntryTypes)) {
      return (
        <div className="py-8 text-center text-gray-500 text-sm border-2 border-dashed rounded-md mt-4">
          Please select an entry type to begin.
        </div>
      );
    }

    switch (entryType) {
      case "calaim":
        return (
          <CalAimEntryForm 
            odrNumber={odrNumber}
            participantId={participantId}
            user={user}
            showHeader={!hasLockedEntryType}
            onSuccess={() => {
              refetch();
              onClose();
            }}
            onCancel={onClose}
          />
        );
      case "coordinated_care":
        return (
          <CoordinatedCareEntryForm
            odrNumber={odrNumber}
            participantId={participantId}
            user={user}
            showHeader={!hasLockedEntryType}
            onSuccess={() => {
              refetch();
              onClose();
            }}
            onCancel={onClose}
          />
        );
      case "general_note":
        return (
          <GeneralNoteEntryForm
            odrNumber={odrNumber}
            participantId={participantId}
            user={user}
            showHeader={!hasLockedEntryType}
            onSuccess={() => {
              refetch();
              onClose();
            }}
            onCancel={onClose}
          />
        );
      case "booking": // New case for booking
        return (
          <AddBookingEntryForm
            odrNumber={odrNumber}
            participantId={participantId}
            refetch={refetch}
            showHeader={!hasLockedEntryType}
            onSuccess={() => {
              refetch();
              onClose();
            }}
            onCancel={onClose}
          />
        );
      case "key_event":
        return (
          <KeyEventEntryForm
            odrNumber={odrNumber}
            participantId={participantId}
            user={user}
            showHeader={!hasLockedEntryType}
            onSuccess={() => {
              refetch();
              onClose();
            }}
            onCancel={onClose}
          />
        );
      case "document":
        return (
          <DocumentEntryForm
            odrNumber={odrNumber}
            user={user}
            showHeader={!hasLockedEntryType}
            onSuccess={() => {
              refetch();
              onClose();
            }}
            onCancel={onClose}
            refetch={refetch}
          />
        );
      default:
        return (
          <div className="py-8 text-center text-gray-500 text-sm border-2 border-dashed rounded-md mt-4">
            Please select an entry type to begin.
          </div>
        );
    }
  };

  const entryTypeOptions = ENTRY_TYPE_OPTIONS.filter((option) =>
    isEntryTypeAllowed(option.value, allowedEntryTypes)
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogContent 
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        contentWidth={hasLockedEntryType ? "1100px" : "95vw"}
        contentMaxWidth="95vw"
        contentHeight="auto"
        contentMaxHeight="90vh"
        className={`overflow-y-auto ${hasLockedEntryType ? "!gap-2 !p-4 !pt-4" : ""}`}
      >
        <DialogHeader className="flex flex-row items-start justify-between">
          <div className="space-y-1">
            <DialogTitle className="text-xl font-bold">
              {getDialogTitle()}
            </DialogTitle>
            <DialogDescription>
              Complete the entry details below and save to update the participant record.
            </DialogDescription>
          </div>
        </DialogHeader>
        
        <div className={hasLockedEntryType ? "" : "space-y-6"}>
          {!hasLockedEntryType && (
            <div className="w-1/3 min-w-[250px]">
              <Label className="mb-2 block">Entry Type</Label>
              <Select value={entryType} onValueChange={handleTypeChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select entry type..." />
                </SelectTrigger>
                <SelectContent>
                  {entryTypeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className={hasLockedEntryType ? "" : "border-t pt-4"}>
            {renderForm()}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
